import { useEffect, useState } from "react";
import type { ProjectManifest } from "@pix/shared";
import { CHARA_POSITIONS, type SceneRow } from "../grid/gridTypes";
import { cacheListByProject, type AssetRecord } from "../storage/assetStore";

interface Props {
  row: SceneRow | null;
  manifest: ProjectManifest;
  projectId: string;
}

/**
 * 選択中行の状態(背景・立ち絵・メッセージ)を簡易的にプレビュー表示する。
 * Phaser を使わずに HTML 合成で再現するため軽量。
 */
export function SampleView({ row, manifest, projectId }: Props) {
  const [objectUrls, setObjectUrls] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    let canceled = false;
    cacheListByProject(projectId).then((list: AssetRecord[]) => {
      if (canceled) return;
      const m = new Map<string, string>();
      for (const a of list) {
        if (a.mime.startsWith("image/")) {
          m.set(a.path, URL.createObjectURL(a.blob));
        }
      }
      setObjectUrls((prev) => {
        for (const url of prev.values()) URL.revokeObjectURL(url);
        return m;
      });
    });
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    return () => {
      for (const url of objectUrls.values()) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!row) {
    return (
      <div className="sample-view sample-view-empty">
        <div className="sample-empty-note">行を選択するとここに状態が表示されます</div>
      </div>
    );
  }

  const bgUrl = row.bg ? objectUrls.get(manifest.backgrounds?.[row.bg.name]?.file ?? "") : undefined;

  return (
    <div className="sample-view">
      <div className="sample-stage">
        {bgUrl ? (
          <img className="sample-bg" src={bgUrl} alt={row.bg?.name ?? ""} />
        ) : (
          <div className="sample-bg-placeholder">
            {row.bg ? `BG: ${row.bg.name}` : "(背景なし)"}
          </div>
        )}
        <div className="sample-charas">
          {CHARA_POSITIONS.map((p) => {
            const c = row.chara[p];
            if (!c) return <div key={p} className={`sample-chara sample-chara-${p}`} />;
            const def = manifest.characters?.[c.id];
            const pattern = def?.spritePattern ?? def?.facePattern;
            const path = pattern
              ? pattern.replaceAll("{expression}", c.expression).replaceAll("{id}", c.id)
              : null;
            const url = path ? objectUrls.get(path) : undefined;
            return (
              <div
                key={p}
                className={`sample-chara sample-chara-${p}`}
                title={`${def?.name ?? c.id} (${c.expression})`}
              >
                {url ? (
                  <img src={url} alt={c.id} />
                ) : (
                  <div className="sample-chara-fallback">
                    {def?.name ?? c.id}
                    <br />
                    <small>{c.expression}</small>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {row.message && (
          <div className="sample-msgbox">
            {row.message.speaker && (
              <div
                className="sample-msg-speaker"
                style={{ color: charaColor(row.message.speaker, manifest) }}
              >
                {row.message.speaker}
              </div>
            )}
            <div className="sample-msg-text">{row.message.plainText}</div>
          </div>
        )}
        {row.choices && (
          <div className="sample-choices">
            {row.choices.map((c, i) => (
              <div key={i} className="sample-choice">
                {c.text}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="sample-meta">
        <div className="sample-meta-row">
          <span className="sample-meta-key">BGM:</span>
          <span className="sample-meta-val">{row.bgm?.file ?? "(なし)"}</span>
        </div>
        {row.se && (
          <div className="sample-meta-row">
            <span className="sample-meta-key">SE:</span>
            <span className="sample-meta-val">{row.se.file}</span>
          </div>
        )}
        <div className="sample-meta-row">
          <span className="sample-meta-key">行:</span>
          <span className="sample-meta-val">{row.sourceLine}</span>
        </div>
        {row.markers.length > 0 && (
          <div className="sample-meta-row">
            <span className="sample-meta-key">マーカー:</span>
            <span className="sample-meta-val">{row.markers.map(markerLabel).join(" / ")}</span>
          </div>
        )}
        {row.variableOps.length > 0 && (
          <div className="sample-meta-row">
            <span className="sample-meta-key">変数:</span>
            <span className="sample-meta-val">
              {row.variableOps.map((o) => `@${o.kind} ${o.raw}`).join(" / ")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function markerLabel(m: SceneRow["markers"][number]): string {
  switch (m.kind) {
    case "label":
      return `##${m.name}`;
    case "jump":
      return `↪${m.target}`;
    case "call":
      return `▸${m.target}`;
    case "return":
      return "◂return";
    case "end":
      return "■end";
    case "choice":
      return "?choice";
    case "macro":
      return `macro:${m.name}`;
    case "wait":
      return `⏱${m.ms !== undefined ? `${m.ms}ms` : ""}`;
    case "transition":
      return `⇄${m.effect}`;
    case "effect":
      return `✨${m.name}`;
    case "screen":
      return `⌬${m.effect}`;
  }
}

function charaColor(speakerName: string, manifest: ProjectManifest): string {
  for (const [, def] of Object.entries(manifest.characters ?? {})) {
    if (def.name === speakerName && def.defaultColor) {
      return def.defaultColor;
    }
  }
  return "#ffd28a";
}

